import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ref, onValue, set, push, remove } from 'firebase/database';
import { database } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import StickyNote from './StickyNote';
import Notepad from './Notepad';
import QuillJournal from './QuillJournal';
import PuzzleGraphic from './PuzzleGraphic';
import DateIdeasNotepad from './DateIdeasNotepad';
import './SharedDesk.css';

const SharedDesk = ({ showNavButtons = false, showButtons = false }) => {
  const navigate = useNavigate();
  const { userProfile, currentUser } = useAuth();
  const [stickyNotes, setStickyNotes] = useState({});
  const [deskLoaded, setDeskLoaded] = useState(false);
  const deskRef = useRef(null);

  useEffect(() => {
    // Delay desk appearance for smooth scroll reveal
    const timer = setTimeout(() => setDeskLoaded(true), 300);
    return () => clearTimeout(timer);
  }, []);

  // Cleanup empty notes when user closes tab or navigates away - DISABLED
  // Note: This feature doesn't work reliably - beforeunload doesn't support async operations
  // TODO: Implement alternative solution (e.g., admin panel cleanup, or timed cleanup)
  // useEffect(() => {
  //   const handleBeforeUnload = async () => {
  //     // Get user's email
  //     const userEmail = currentUser?.email;
  //     if (!userEmail) return;

  //     // Find and delete all empty notes created by this user
  //     const notesToDelete = Object.entries(stickyNotes).filter(
  //       ([id, note]) =>
  //         note.createdByEmail === userEmail &&
  //         (!note.text || note.text.trim() === '')
  //     );

  //     // Delete each empty note
  //     for (const [id, note] of notesToDelete) {
  //       try {
  //         const noteRef = ref(database, `desk/stickyNotes/${id}`);
  //         await remove(noteRef);
  //       } catch (error) {
  //         console.error('Error cleaning up empty note:', error);
  //       }
  //     }
  //   };

  //   window.addEventListener('beforeunload', handleBeforeUnload);

  //   return () => {
  //     window.removeEventListener('beforeunload', handleBeforeUnload);
  //     // Also cleanup on component unmount
  //     handleBeforeUnload();
  //   };
  // }, [stickyNotes, currentUser]);

  // Real-time sync of sticky notes from Firebase
  useEffect(() => {
    const notesRef = ref(database, 'desk/stickyNotes');

    const unsubscribe = onValue(notesRef, (snapshot) => {
      if (snapshot.exists()) {
        setStickyNotes(snapshot.val());
      } else {
        setStickyNotes({});
      }
    });

    return () => unsubscribe();
  }, []);

  const handleCreateNote = async (position) => {
    try {
      const notesRef = ref(database, 'desk/stickyNotes');
      const newNoteRef = push(notesRef);

      const newNote = {
        text: '',
        position: position || { x: 100, y: 100 },
        color: getRandomNoteColor(),
        createdBy: userProfile?.name || currentUser?.email || 'anonymous',
        createdByEmail: currentUser?.email || 'anonymous',
        createdAt: new Date().toISOString(),
      };

      await set(newNoteRef, newNote);
    } catch (error) {
      console.error('Error creating sticky note:', error);
    }
  };

  const handleUpdateNote = async (noteId, updates) => {
    try {
      const noteRef = ref(database, `desk/stickyNotes/${noteId}`);
      await set(noteRef, {
        ...stickyNotes[noteId],
        ...updates,
      });
    } catch (error) {
      console.error('Error updating sticky note:', error);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      const noteRef = ref(database, `desk/stickyNotes/${noteId}`);
      await remove(noteRef);
    } catch (error) {
      console.error('Error deleting sticky note:', error);
    }
  };

  // Pin a sticky note to the wall
  const handlePinNote = async (noteId, noteData) => {
    try {
      // Define forbidden zones where wall elements exist (as percentage ranges)
      // Based on grid layout: 12 columns, 8 rows
      const forbiddenZones = [
        // Trail painting: grid-column 2/6, grid-row 2/6 = 8-42% width, 12-62% height
        { x: [8, 42], y: [12, 62] },
        // Clock: grid-column 7/9, grid-row 2/4 = 50-67% width, 12-37% height
        { x: [50, 67], y: [12, 37] },
        // Picture frame: grid-column 9/12, grid-row 3/6 = 67-92% width, 25-62% height
        { x: [67, 92], y: [25, 62] },
        // Footprint painting: grid-column 7/9, grid-row 4/7 = 50-67% width, 37-75% height
        { x: [50, 67], y: [37, 75] },
        // Calendar: grid-column 2/4, grid-row 6/8 = 8-25% width, 62-87% height
        { x: [8, 25], y: [62, 87] },
        // Yo sign: grid-column 10/12, grid-row 6/7 = 75-92% width, 62-75% height
        { x: [75, 92], y: [62, 75] },
      ];

      // Function to check if a position overlaps with forbidden zones
      const isPositionValid = (x, y) => {
        const noteWidth = 14; // Pinned note is ~140px, assume viewport ~1000px = 14%
        const noteHeight = 14;

        for (const zone of forbiddenZones) {
          // Check if note overlaps with this zone
          if (
            x + noteWidth > zone.x[0] && x < zone.x[1] &&
            y + noteHeight > zone.y[0] && y < zone.y[1]
          ) {
            return false; // Overlaps with forbidden zone
          }
        }
        return true; // Position is valid
      };

      // Generate random position, retry if it overlaps
      let wallPosition;
      let attempts = 0;
      const maxAttempts = 50;

      do {
        wallPosition = {
          x: Math.random() * 80 + 5,  // 5-85% from left
          y: Math.random() * 75 + 10, // 10-85% from top
          rotation: (Math.random() - 0.5) * 15, // -7.5 to 7.5 degrees
        };
        attempts++;
      } while (!isPositionValid(wallPosition.x, wallPosition.y) && attempts < maxAttempts);

      // If couldn't find valid position after max attempts, place in safe corners
      if (attempts >= maxAttempts) {
        const corners = [
          { x: 5, y: 10 },   // Top left
          { x: 85, y: 10 },  // Top right
          { x: 5, y: 85 },   // Bottom left
          { x: 85, y: 85 },  // Bottom right
        ];
        const corner = corners[Math.floor(Math.random() * corners.length)];
        wallPosition = {
          ...corner,
          rotation: (Math.random() - 0.5) * 15,
        };
      }

      // Save to wall/pinnedNotes in Firebase
      const pinnedNotesRef = ref(database, 'wall/pinnedNotes');
      const newPinnedRef = push(pinnedNotesRef);

      const pinnedNote = {
        ...noteData,
        pinnedAt: new Date().toISOString(),
        wallPosition,
        originalNoteId: noteId,
      };

      await set(newPinnedRef, pinnedNote);

      // Remove the original note from the desk
      const noteRef = ref(database, `desk/stickyNotes/${noteId}`);
      await remove(noteRef);

      console.log('Note pinned to wall successfully');
    } catch (error) {
      console.error('Error pinning note to wall:', error);
    }
  };

  const getRandomNoteColor = () => {
    const colors = [
      'var(--color-watercolor-yellow)',
      'var(--color-watercolor-pink)',
      'var(--color-watercolor-blue)',
      'var(--color-watercolor-green)',
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  return (
    <motion.div
      ref={deskRef}
      className="shared-desk"
      initial={{ opacity: 0 }}
      animate={{ opacity: deskLoaded ? 1 : 0 }}
      transition={{ duration: 1 }}
    >
      {/* "Your Desk" Label */}
      <motion.div
        className="desk-label"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: deskLoaded ? 1 : 0, y: deskLoaded ? 0 : -20 }}
        transition={{ delay: 0.5, duration: 0.8 }}
      >
        <div className="desk-label-paper">
          <p className="desk-label-text">Our Desk</p>
        </div>
      </motion.div>

      {/* Desk Background */}
      <div className="desk-background">
        <div className="desk-wood-texture" />
        <div className="desk-gradient" />
      </div>

      {/* Desk Surface - This is where sticky notes live */}
      <div className="desk-surface">
        {/* Render all sticky notes with AnimatePresence for smooth entry/exit */}
        <AnimatePresence mode="popLayout">
          {Object.entries(stickyNotes).map(([id, note]) => (
            <StickyNote
              key={id}
              id={id}
              note={note}
              onUpdate={(updates) => handleUpdateNote(id, updates)}
              onDelete={() => handleDeleteNote(id)}
              onPin={handlePinNote}
            />
          ))}
        </AnimatePresence>
      </div>

      {/* Interactive Desk Items */}
      <motion.div
        className="desk-items"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: deskLoaded ? 1 : 0, y: deskLoaded ? 0 : 20 }}
        transition={{ delay: 0.3, duration: 0.8 }}
      >
        {/* Notepad - Click to create new sticky notes */}
        <div className="notepad-container">
          <Notepad onCreate={handleCreateNote} />
        </div>

        {/* Quill & Ink - Click to go to Journal page */}
        <div className="quill-container">
          <QuillJournal onClick={() => navigate('/journal')} />
        </div>

        {/* Puzzle Graphic - Click to go to Puzzle page */}
        <div className="puzzle-container">
          <PuzzleGraphic onClick={() => navigate('/puzzle')} />
        </div>

        {/* Date Ideas Notepad - Click to go to Date Ideas page */}
        <div className="date-ideas-container">
          <DateIdeasNotepad />
        </div>
      </motion.div>

      {/* Scroll hint or navigation buttons at bottom - only show if showNavButtons is true */}
      {showNavButtons && (
        <AnimatePresence mode="wait">
          {!showButtons ? (
            <motion.div
              key="scroll-hint"
              className="scroll-hint"
              initial={{ opacity: 0 }}
              animate={{ opacity: deskLoaded ? 1 : 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <motion.div
                className="hint-arrow"
                animate={{ y: [0, 10, 0] }}
                transition={{ repeat: Infinity, duration: 2 }}
              >
                ↓
              </motion.div>
              <p>Scroll to explore more</p>
            </motion.div>
          ) : (
            <motion.div
              key="nav-buttons"
              className="desk-nav-buttons"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {/* Letters button - cute parchment envelope from left */}
              <motion.button
                className="desk-nav-button letters-button"
                onClick={() => navigate('/letters')}
                initial={{ opacity: 0, x: -150, rotate: -10 }}
                animate={{ opacity: 1, x: 0, rotate: 0 }}
                exit={{ opacity: 0, x: -150, rotate: -10 }}
                transition={{ duration: 0.5, type: "spring", stiffness: 120, damping: 15 }}
                whileHover={{ scale: 1.1, rotate: -3, y: -5 }}
                whileTap={{ scale: 0.95 }}
              >
                <svg viewBox="0 0 60 50" width="60" height="50" className="button-icon">
                  {/* Parchment envelope body */}
                  <path
                    d="M 5,15 L 55,15 L 55,45 L 5,45 Z"
                    fill="#f8f4e8"
                    stroke="#c4a77d"
                    strokeWidth="2"
                  />
                  {/* Envelope inner shadow */}
                  <path
                    d="M 5,15 L 30,32 L 55,15"
                    fill="#efe8d8"
                    stroke="none"
                  />
                  {/* Envelope flap (triangular top) */}
                  <path
                    d="M 5,15 L 30,32 L 55,15 L 55,8 Q 55,5 52,5 L 8,5 Q 5,5 5,8 Z"
                    fill="#f5f0e0"
                    stroke="#c4a77d"
                    strokeWidth="2"
                  />
                  {/* Wax seal with heart */}
                  <circle cx="30" cy="12" r="8" fill="#d4737a" stroke="#b85a61" strokeWidth="1.5" />
                  <path
                    d="M 30,8 Q 27,6 25,8 Q 23,11 25,13 L 30,17 L 35,13 Q 37,11 35,8 Q 33,6 30,8 Z"
                    fill="#e89a9f"
                    stroke="none"
                  />
                  {/* Paper edge detail */}
                  <path
                    d="M 8,45 Q 10,43 12,45 Q 14,47 16,45 Q 18,43 20,45 Q 22,47 24,45 Q 26,43 28,45 Q 30,47 32,45 Q 34,43 36,45 Q 38,47 40,45 Q 42,43 44,45 Q 46,47 48,45 Q 50,43 52,45"
                    fill="none"
                    stroke="#d4c4a8"
                    strokeWidth="1"
                  />
                </svg>
                <span>Letters</span>
              </motion.button>

              {/* Tape Recorder button - from bottom (middle) */}
              <motion.button
                className="desk-nav-button cassette-button"
                onClick={() => navigate('/voice-memos')}
                initial={{ opacity: 0, y: 80, scale: 0.8 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 80, scale: 0.8 }}
                transition={{ duration: 0.5, type: "spring", stiffness: 120, damping: 15, delay: 0.1 }}
                whileHover={{ scale: 1.1, y: -5 }}
                whileTap={{ scale: 0.95 }}
              >
                <svg viewBox="0 0 60 52" width="60" height="52" className="button-icon">
                  {/* Carrying straps - teal/blue */}
                  <rect x="1" y="18" width="6" height="16" rx="1.5" fill="#88c8c8" />
                  <rect x="53" y="18" width="6" height="16" rx="1.5" fill="#88c8c8" />
                  <rect x="0" y="20" width="4" height="12" rx="1" fill="#a8d8d8" />
                  <rect x="56" y="20" width="4" height="12" rx="1" fill="#a8d8d8" />

                  {/* Main wooden body */}
                  <rect x="7" y="8" width="46" height="40" rx="5" fill="#e8d4b8" stroke="#d4c0a0" strokeWidth="1" />
                  <rect x="9" y="10" width="42" height="36" rx="4" fill="#f0e0c8" opacity="0.6" />

                  {/* Top wooden knobs */}
                  {/* Left knob */}
                  <ellipse cx="21" cy="8" rx="5" ry="1.5" fill="#d4c0a0" />
                  <rect x="16" y="2" width="10" height="6" rx="1.5" fill="#e8d4b8" stroke="#d4c0a0" strokeWidth="0.8" />
                  <ellipse cx="21" cy="2" rx="5" ry="1.5" fill="#f0e0c8" />

                  {/* Right knob with triangle */}
                  <ellipse cx="39" cy="8" rx="5" ry="1.5" fill="#d4c0a0" />
                  <rect x="34" y="2" width="10" height="6" rx="1.5" fill="#e8d4b8" stroke="#d4c0a0" strokeWidth="0.8" />
                  <ellipse cx="39" cy="2" rx="5" ry="1.5" fill="#f0e0c8" />
                  <polygon points="39,1 37.5,3.5 40.5,3.5" fill="#d4c0a0" />

                  {/* Pink front panel */}
                  <rect x="12" y="13" width="36" height="30" rx="3" fill="#fce4ec" stroke="#f0c0d0" strokeWidth="0.8" />

                  {/* Green cassette door */}
                  <rect x="15" y="17" width="24" height="22" rx="2" fill="#b8e8a8" stroke="#98d888" strokeWidth="0.8" />

                  {/* Circular speaker grille */}
                  <circle cx="25" cy="28" r="8" fill="#c8f0b8" stroke="#a8d898" strokeWidth="0.6" />
                  <circle cx="25" cy="28" r="2" fill="#f8f8f0" stroke="#d0d0c0" strokeWidth="0.4" />
                  {/* Speaker holes in circle */}
                  <g fill="#98c888">
                    <circle cx="25" cy="22" r="1" />
                    <circle cx="29" cy="24" r="1" />
                    <circle cx="31" cy="28" r="1" />
                    <circle cx="29" cy="32" r="1" />
                    <circle cx="25" cy="34" r="1" />
                    <circle cx="21" cy="32" r="1" />
                    <circle cx="19" cy="28" r="1" />
                    <circle cx="21" cy="24" r="1" />
                  </g>

                  {/* Small dial on green panel */}
                  <circle cx="35" cy="34" r="3" fill="#f8f8f0" stroke="#d0d0c0" strokeWidth="0.6" />
                  <circle cx="35" cy="34" r="1.5" fill="#e0e0d8" />
                  <circle cx="35" cy="34" r="0.6" fill="#88c8c8" />

                  {/* Recording indicator */}
                  <circle cx="44" cy="20" r="2" fill="#f8d8d8" stroke="#e8b8b8" strokeWidth="0.4" />
                  <circle cx="44" cy="20" r="1" fill="#e88888" />
                </svg>
                <span>Voice Memos</span>
              </motion.button>

              {/* Poetry button - cute fancy parchment scroll from right */}
              <motion.button
                className="desk-nav-button poetry-button"
                onClick={() => navigate('/poetry')}
                initial={{ opacity: 0, x: 150, rotate: 10 }}
                animate={{ opacity: 1, x: 0, rotate: 0 }}
                exit={{ opacity: 0, x: 150, rotate: 10 }}
                transition={{ duration: 0.5, type: "spring", stiffness: 120, damping: 15 }}
                whileHover={{ scale: 1.1, rotate: 3, y: -5 }}
                whileTap={{ scale: 0.95 }}
              >
                <svg viewBox="0 0 50 60" width="50" height="60" className="button-icon">
                  {/* Parchment scroll body */}
                  <path
                    d="M 8,8 L 42,8 L 42,52 L 8,52 Z"
                    fill="#f8f4e8"
                    stroke="#c4a77d"
                    strokeWidth="2"
                  />
                  {/* Scroll curl top */}
                  <ellipse cx="8" cy="8" rx="4" ry="3" fill="#efe8d8" stroke="#c4a77d" strokeWidth="1.5" />
                  <ellipse cx="42" cy="8" rx="4" ry="3" fill="#efe8d8" stroke="#c4a77d" strokeWidth="1.5" />
                  {/* Scroll curl bottom */}
                  <ellipse cx="8" cy="52" rx="4" ry="3" fill="#efe8d8" stroke="#c4a77d" strokeWidth="1.5" />
                  <ellipse cx="42" cy="52" rx="4" ry="3" fill="#efe8d8" stroke="#c4a77d" strokeWidth="1.5" />

                  {/* Decorative header flourish */}
                  <path
                    d="M 15,14 Q 25,10 35,14"
                    fill="none"
                    stroke="#d4a07a"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <circle cx="15" cy="14" r="1.5" fill="#d4a07a" />
                  <circle cx="35" cy="14" r="1.5" fill="#d4a07a" />

                  {/* Poetry text lines */}
                  <line x1="14" y1="22" x2="36" y2="22" stroke="#c4a77d" strokeWidth="1.2" opacity="0.6" />
                  <line x1="14" y1="28" x2="34" y2="28" stroke="#c4a77d" strokeWidth="1.2" opacity="0.6" />
                  <line x1="14" y1="34" x2="32" y2="34" stroke="#c4a77d" strokeWidth="1.2" opacity="0.6" />
                  <line x1="14" y1="40" x2="30" y2="40" stroke="#c4a77d" strokeWidth="1.2" opacity="0.6" />

                  {/* Decorative footer flourish with heart */}
                  <path
                    d="M 18,46 Q 25,50 32,46"
                    fill="none"
                    stroke="#d9989f"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <path
                    d="M 25,47 Q 23,45 21,47 Q 20,49 21,50 L 25,53 L 29,50 Q 30,49 29,47 Q 27,45 25,47 Z"
                    fill="#d9989f"
                    stroke="none"
                  />
                </svg>
                <span>Poetry</span>
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </motion.div>
  );
};

export default SharedDesk;
